import React, { useState, useCallback, useRef } from "react";
import { ButtonH } from "components/UiKit/ButtonH";
import { Ico } from "components/Uikit";
import { useAudioStateContext } from "hooks/useAudioStateContext";
import { useAudioHandlesContext } from "hooks/useAudioHandlesContext";
import {
    translationPostProcessScanStream,
    translationPostProcessApplyApi,
    PostProcessProposal,
    ScanEvent,
} from "api/fastApi/translationPostProcess";
import { message, Modal, Checkbox, Tag, Tooltip, Radio } from "antd";
import { MetadataToSubtitlesLanguages } from "providers/AudioProvider/types";
import { useIsEnglishUi } from "utils/uiLanguage";
import { AutoEditGate, useAutoEditBlock } from "../../../ManualOnly/AutoEditGate";

// ── Spin animation keyframes ──────────────────────────
const spinKeyframes = `
@keyframes tppSpin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
}
`;

// ── Word-level diff ───────────────────────────────────

interface DiffSegment {
    type: "same" | "added" | "removed";
    text: string;
}

function computeWordDiff(oldText: string, newText: string): DiffSegment[] {
    const oldWords = oldText.split(/\s+/).filter(Boolean);
    const newWords = newText.split(/\s+/).filter(Boolean);

    // Build LCS table
    const m = oldWords.length;
    const n = newWords.length;
    const dp: number[][] = Array.from({ length: m + 1 }, () => Array(n + 1).fill(0));

    for (let i = 1; i <= m; i++) {
        for (let j = 1; j <= n; j++) {
            if (oldWords[i - 1] === newWords[j - 1]) {
                dp[i][j] = dp[i - 1][j - 1] + 1;
            } else {
                dp[i][j] = Math.max(dp[i - 1][j], dp[i][j - 1]);
            }
        }
    }

    // Backtrack to produce diff segments
    const segments: DiffSegment[] = [];
    let i = m;
    let j = n;
    const raw: DiffSegment[] = [];

    while (i > 0 || j > 0) {
        if (i > 0 && j > 0 && oldWords[i - 1] === newWords[j - 1]) {
            raw.push({ type: "same", text: oldWords[i - 1] });
            i--;
            j--;
        } else if (j > 0 && (i === 0 || dp[i][j - 1] >= dp[i - 1][j])) {
            raw.push({ type: "added", text: newWords[j - 1] });
            j--;
        } else {
            raw.push({ type: "removed", text: oldWords[i - 1] });
            i--;
        }
    }

    raw.reverse();

    // Merge consecutive segments of same type
    for (const seg of raw) {
        if (segments.length > 0 && segments[segments.length - 1].type === seg.type) {
            segments[segments.length - 1].text += " " + seg.text;
        } else {
            segments.push({ ...seg });
        }
    }

    return segments;
}

// ── Step definitions ──────────────────────────────────

interface StepDef {
    key: string;
    icon: string;
    labelEn: string;
    labelRu: string;
}

const STEPS: StepDef[] = [
    { key: "speech_cleanup", icon: "cleaning_services", labelEn: "Speech Cleanup", labelRu: "Очистка речи" },
    { key: "pronouns_grammar", icon: "person", labelEn: "Pronouns", labelRu: "Местоимения" },
    { key: "terms_cultural", icon: "menu_book", labelEn: "Terms", labelRu: "Термины" },
    { key: "style_naturalness", icon: "auto_fix_high", labelEn: "Style", labelRu: "Стиль" },
];

// ── Phase type ────────────────────────────────────────

type Phase = "idle" | "scanning" | "proposals" | "applying" | "done";

// ── Extended proposal with user choices ───────────────

interface ProposalRow extends PostProcessProposal {
    checked: boolean;
    useAlt: boolean; // true = use alt_text instead of new_text
}

// ── Component ─────────────────────────────────────────

export const TranslationPostProcess: React.FC = () => {
    const state = useAudioStateContext();
    const handles = useAudioHandlesContext();

    const fromEng = useIsEnglishUi();

    // Modal state
    const [showModal, setShowModal] = useState(false);
    const [minimized, setMinimized] = useState(false);
    const [activeStep, setActiveStep] = useState<StepDef | null>(null);
    const [phase, setPhase] = useState<Phase>("idle");
    const [loadingStep, setLoadingStep] = useState<string | null>(null);

    // Scan results
    const [proposals, setProposals] = useState<ProposalRow[]>([]);
    const [totalScanned, setTotalScanned] = useState(0);

    // Apply results
    const [applyResult, setApplyResult] = useState<{ applied: number; failed: number } | null>(null);

    // Dragging
    const [offsetPos, setOffsetPos] = useState<{ dx: number; dy: number }>({ dx: 0, dy: 0 });
    const dragRef = useRef<{ startX: number; startY: number; startDx: number; startDy: number } | null>(null);

    // Refresh tracking
    const needsRefreshRef = useRef(false);

    // ── Labels ────────────────────────────────────────
    const labels = {
        modalTitle: fromEng ? "Translation Post-Processing" : "Пост-обработка перевода",
        scanning: fromEng ? "Scanning" : "Сканирование",
        subtitles: fromEng ? "subtitles" : "субтитров",
        noChanges: fromEng ? "No changes proposed" : "Изменений не предложено",
        selectAll: fromEng ? "Select all" : "Выбрать все",
        deselectAll: fromEng ? "Deselect all" : "Снять все",
        selectHigh: fromEng ? "Select high only" : "Только надёжные",
        apply: fromEng ? "Apply" : "Применить",
        selected: fromEng ? "selected" : "выбрано",
        applying: fromEng ? "Applying changes..." : "Применяем изменения...",
        done: fromEng ? "Done" : "Готово",
        applied: fromEng ? "applied" : "применено",
        failedLabel: fromEng ? "failed" : "ошибок",
        close: fromEng ? "Close" : "Закрыть",
        scanError: fromEng ? "Scan failed" : "Ошибка сканирования",
        applyError: fromEng ? "Apply failed" : "Ошибка применения",
        bothAgree: fromEng ? "Both agree" : "Оба согласны",
        oneModel: fromEng ? "One model" : "Одна модель",
        original: fromEng ? "Original" : "Оригинал",
        proposed: fromEng ? "Proposed" : "Предложено",
        alternative: fromEng ? "Alternative" : "Альтернатива",
        runAgain: fromEng ? "Run again" : "Запустить снова",
    };

    // ── Scan (streaming) ─────────────────────────────
    const [scanProgress, setScanProgress] = useState<{ current: number; total: number }>({ current: 0, total: 0 });

    const runScan = useCallback(async (step: StepDef) => {
        if (!state.mediaId || !state.folder || !state.lang) {
            message.error(labels.scanError);
            return;
        }

        setActiveStep(step);
        setPhase("scanning");
        setLoadingStep(step.key);
        setShowModal(true);
        setMinimized(false);
        setProposals([]);
        setApplyResult(null);
        setScanProgress({ current: 0, total: 0 });

        const collectedProposals: ProposalRow[] = [];

        try {
            await translationPostProcessScanStream(
                {
                    mediaId: state.mediaId,
                    folderNum: parseInt(state.folder.toString(), 10),
                    lang: state.lang,
                    languageFrom: MetadataToSubtitlesLanguages[state.originalLang],
                    step: step.key,
                },
                (event: ScanEvent) => {
                    if (event.type === "progress") {
                        setScanProgress({ current: event.current, total: event.total });
                    } else if (event.type === "proposal") {
                        const row: ProposalRow = {
                            subtitle_id: event.subtitle_id,
                            old_text: event.old_text,
                            new_text: event.new_text,
                            alt_text: event.alt_text,
                            confidence: event.confidence,
                            checked: event.confidence === "high",
                            useAlt: false,
                        };
                        collectedProposals.push(row);
                        setProposals([...collectedProposals]);
                    } else if (event.type === "done") {
                        setTotalScanned(event.total_scanned);
                    } else if (event.type === "error") {
                        message.error(event.detail || labels.scanError);
                    }
                },
            );

            if (collectedProposals.length === 0) {
                message.info(labels.noChanges);
            }
            setPhase("proposals");
        } catch (error: any) {
            console.error("Translation post-process scan error:", error);
            message.error(labels.scanError);
            setPhase("idle");
            setShowModal(false);
        } finally {
            setLoadingStep(null);
        }
    }, [state.mediaId, state.folder, state.lang, state.originalLang]);

    // ── Apply ─────────────────────────────────────────
    // Проверка только предлагает — она работает всегда. «Применить» пишет машинный текст поверх
    // перевода человека (отмеченные «high» — одним нажатием) — в ручном режиме выключено.
    const autoEdit = useAutoEditBlock();
    const runApply = useCallback(async () => {
        if (autoEdit.blocked) return;
        if (!state.mediaId || !state.folder || !state.lang || !activeStep) {
            message.error(labels.applyError);
            return;
        }

        const selected = proposals.filter((p) => p.checked);
        if (selected.length === 0) return;

        setPhase("applying");

        try {
            const changes = selected.map((p) => ({
                subtitle_id: p.subtitle_id,
                new_text: p.useAlt && p.alt_text ? p.alt_text : p.new_text,
            }));

            const res = await translationPostProcessApplyApi({
                mediaId: state.mediaId,
                folderNum: parseInt(state.folder.toString(), 10),
                lang: state.lang,
                languageFrom: MetadataToSubtitlesLanguages[state.originalLang],
                step: activeStep.key,
                changes,
            });

            setApplyResult(res);
            setPhase("done");
            needsRefreshRef.current = true;
            message.success(
                `${res.applied} ${labels.applied}${res.failed > 0 ? `, ${res.failed} ${labels.failedLabel}` : ""}`
            );
        } catch (error: any) {
            console.error("Translation post-process apply error:", error);
            message.error(labels.applyError);
            setPhase("proposals");
        }
    }, [state.mediaId, state.folder, state.lang, state.originalLang, activeStep, proposals, autoEdit.blocked]);

    // ── Selection helpers ─────────────────────────────
    const toggleProposal = (idx: number) => {
        setProposals((prev) => {
            const next = [...prev];
            next[idx] = { ...next[idx], checked: !next[idx].checked };
            return next;
        });
    };

    const toggleAlt = (idx: number) => {
        setProposals((prev) => {
            const next = [...prev];
            next[idx] = { ...next[idx], useAlt: !next[idx].useAlt };
            return next;
        });
    };

    const selectAll = () => {
        setProposals((prev) => prev.map((p) => ({ ...p, checked: true })));
    };

    const deselectAll = () => {
        setProposals((prev) => prev.map((p) => ({ ...p, checked: false })));
    };

    const selectHighOnly = () => {
        setProposals((prev) =>
            prev.map((p) => ({ ...p, checked: p.confidence === "high" }))
        );
    };

    const selectedCount = proposals.filter((p) => p.checked).length;

    // ── Drag handler ──────────────────────────────────
    const onTitleMouseDown = (e: React.MouseEvent) => {
        e.preventDefault();
        const startX = e.clientX;
        const startY = e.clientY;
        dragRef.current = { startX, startY, startDx: offsetPos.dx, startDy: offsetPos.dy };

        const onMove = (ev: MouseEvent) => {
            if (!dragRef.current) return;
            setOffsetPos({
                dx: dragRef.current.startDx + (ev.clientX - dragRef.current.startX),
                dy: dragRef.current.startDy + (ev.clientY - dragRef.current.startY),
            });
        };
        const onUp = () => {
            dragRef.current = null;
            document.removeEventListener("mousemove", onMove);
            document.removeEventListener("mouseup", onUp);
        };
        document.addEventListener("mousemove", onMove);
        document.addEventListener("mouseup", onUp);
    };

    // ── Close handler ─────────────────────────────────
    const handleClose = () => {
        setShowModal(false);
        setPhase("idle");
        setActiveStep(null);
        setProposals([]);
        setApplyResult(null);
        if (needsRefreshRef.current) {
            needsRefreshRef.current = false;
            handles.refreshAllSubtitles();
        }
    };

    // ── Render diff segments ──────────────────────────
    const renderDiff = (oldText: string, newText: string) => {
        const segments = computeWordDiff(oldText, newText);
        return (
            <span>
                {segments.map((seg, i) => {
                    if (seg.type === "removed") {
                        return (
                            <span
                                key={i}
                                style={{
                                    backgroundColor: "#ffd7d5",
                                    textDecoration: "line-through",
                                    padding: "1px 2px",
                                    borderRadius: 2,
                                    marginRight: 2,
                                }}
                            >
                                {seg.text}
                            </span>
                        );
                    }
                    if (seg.type === "added") {
                        return (
                            <span
                                key={i}
                                style={{
                                    backgroundColor: "#d4edda",
                                    padding: "1px 2px",
                                    borderRadius: 2,
                                    marginRight: 2,
                                }}
                            >
                                {seg.text}
                            </span>
                        );
                    }
                    return (
                        <span key={i} style={{ marginRight: 2 }}>
                            {seg.text}
                        </span>
                    );
                })}
            </span>
        );
    };

    // ── Render proposal row ───────────────────────────
    const renderProposalRow = (row: ProposalRow, idx: number) => {
        const chosenText = row.useAlt && row.alt_text ? row.alt_text : row.new_text;

        return (
            <div
                key={row.subtitle_id}
                style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 10,
                    padding: "8px 12px",
                    backgroundColor: row.checked ? "#fafffe" : "#fafafa",
                    border: `1px solid ${row.checked ? "#b7eb8f" : "#e8e8e8"}`,
                    borderRadius: 6,
                    marginBottom: 6,
                    transition: "border-color 0.2s",
                }}
            >
                {/* Checkbox */}
                <div style={{ paddingTop: 2 }}>
                    <Checkbox
                        checked={row.checked}
                        onChange={() => toggleProposal(idx)}
                    />
                </div>

                {/* Confidence badge */}
                <div style={{ minWidth: 90, paddingTop: 2 }}>
                    <Tag
                        color={row.confidence === "high" ? "green" : "gold"}
                        style={{ fontSize: 11, margin: 0 }}
                    >
                        {row.confidence === "high" ? labels.bothAgree : labels.oneModel}
                    </Tag>
                </div>

                {/* Text diff area */}
                <div style={{ flex: 1, minWidth: 0 }}>
                    {/* Original text */}
                    <div style={{ fontSize: 12, color: "#888", marginBottom: 4 }}>
                        <span style={{ fontWeight: 600, color: "#999", marginRight: 4 }}>
                            {labels.original}:
                        </span>
                        {row.old_text}
                    </div>

                    {/* Proposed diff */}
                    <div style={{ fontSize: 13, marginBottom: row.alt_text ? 6 : 0 }}>
                        <span style={{ fontWeight: 600, color: "#666", marginRight: 4 }}>
                            {"\u2192"}
                        </span>
                        {renderDiff(row.old_text, chosenText)}
                    </div>

                    {/* Alt text toggle */}
                    {row.alt_text && (
                        <div style={{ marginTop: 4, paddingTop: 4, borderTop: "1px solid #f0f0f0" }}>
                            <Radio.Group
                                size="small"
                                value={row.useAlt ? "alt" : "main"}
                                onChange={() => toggleAlt(idx)}
                            >
                                <Radio value="main" style={{ fontSize: 12 }}>
                                    <Tooltip title={row.new_text}>
                                        <span>{labels.proposed}</span>
                                    </Tooltip>
                                </Radio>
                                <Radio value="alt" style={{ fontSize: 12 }}>
                                    <Tooltip title={row.alt_text}>
                                        <span>{labels.alternative}</span>
                                    </Tooltip>
                                </Radio>
                            </Radio.Group>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    // ── Render modal body by phase ────────────────────
    const renderModalBody = () => {
        if (!activeStep) return null;

        const stepLabel = fromEng ? activeStep.labelEn : activeStep.labelRu;

        // Phase: scanning
        if (phase === "scanning") {
            return (
                <div style={{ textAlign: "center", padding: "40px 20px" }}>
                    <Ico
                        size={"32"}
                        style={{
                            animation: "tppSpin 1s linear infinite",
                            display: "inline-block",
                            color: "#1890ff",
                            marginBottom: 16,
                        }}
                    >
                        hourglass_top
                    </Ico>
                    <div style={{ fontSize: 16, fontWeight: 500 }}>
                        {labels.scanning} {labels.subtitles}...
                        {scanProgress.total > 0 && (
                            <span style={{ marginLeft: 8, color: "#1890ff" }}>
                                {scanProgress.current}/{scanProgress.total}
                            </span>
                        )}
                    </div>
                    {scanProgress.total > 0 && (
                        <div style={{ width: "80%", margin: "12px auto", background: "#f0f0f0", borderRadius: 4, height: 6 }}>
                            <div style={{
                                width: `${(scanProgress.current / scanProgress.total) * 100}%`,
                                background: "#1890ff",
                                borderRadius: 4,
                                height: 6,
                                transition: "width 0.3s",
                            }} />
                        </div>
                    )}
                    <div style={{ fontSize: 13, color: "#888", marginTop: 8 }}>
                        {stepLabel}
                        {proposals.length > 0 && ` — ${proposals.length} ${fromEng ? "changes found" : "изменений найдено"}`}
                    </div>
                </div>
            );
        }

        // Phase: proposals
        if (phase === "proposals") {
            if (proposals.length === 0) {
                return (
                    <div style={{ textAlign: "center", padding: 24 }}>
                        <Tag color="green" style={{ fontSize: 14, padding: "4px 12px" }}>
                            {labels.noChanges}
                        </Tag>
                        <div style={{ fontSize: 12, color: "#888", marginTop: 8 }}>
                            {totalScanned} {labels.subtitles}
                        </div>
                    </div>
                );
            }

            return (
                <div>
                    {/* Summary bar */}
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            marginBottom: 12,
                            padding: "8px 12px",
                            backgroundColor: "#fafafa",
                            border: "1px solid #e8e8e8",
                            borderRadius: 6,
                        }}
                    >
                        <span style={{ fontSize: 13, color: "#666" }}>
                            <strong>{proposals.length}</strong>{" "}
                            {fromEng ? "proposals" : "предложений"}
                            {" \u2022 "}
                            <strong>{selectedCount}</strong> {labels.selected}
                            {" \u2022 "}
                            {totalScanned} {labels.subtitles}
                        </span>
                        <div style={{ display: "flex", gap: 6 }}>
                            <ButtonH sm ghost onClick={selectAll}>
                                {labels.selectAll}
                            </ButtonH>
                            <ButtonH sm ghost onClick={deselectAll}>
                                {labels.deselectAll}
                            </ButtonH>
                            <ButtonH sm ghost onClick={selectHighOnly}>
                                {labels.selectHigh}
                            </ButtonH>
                        </div>
                    </div>

                    {/* Proposals list */}
                    <div
                        style={{
                            maxHeight: 450,
                            overflowY: "auto",
                            paddingRight: 4,
                        }}
                    >
                        {proposals.map((row, idx) => renderProposalRow(row, idx))}
                    </div>

                    {/* Apply bar */}
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "flex-end",
                            alignItems: "center",
                            gap: 8,
                            marginTop: 12,
                            paddingTop: 12,
                            borderTop: "1px solid #f0f0f0",
                        }}
                    >
                        <ButtonH sm ghost onClick={handleClose}>
                            {labels.close}
                        </ButtonH>
                        <AutoEditGate {...autoEdit}>
                            <ButtonH
                                sm
                                onClick={runApply}
                                disabled={selectedCount === 0 || autoEdit.blocked}
                            >
                                {labels.apply} {selectedCount} {labels.selected}
                            </ButtonH>
                        </AutoEditGate>
                    </div>
                </div>
            );
        }

        // Phase: applying
        if (phase === "applying") {
            return (
                <div style={{ textAlign: "center", padding: "40px 20px" }}>
                    <Ico
                        size={"32"}
                        style={{
                            animation: "tppSpin 1s linear infinite",
                            display: "inline-block",
                            color: "#1890ff",
                            marginBottom: 16,
                        }}
                    >
                        hourglass_top
                    </Ico>
                    <div style={{ fontSize: 16, fontWeight: 500 }}>
                        {labels.applying}
                    </div>
                    <div style={{ fontSize: 13, color: "#888", marginTop: 8 }}>
                        {selectedCount} {fromEng ? "changes" : "изменений"}
                    </div>
                </div>
            );
        }

        // Phase: done
        if (phase === "done" && applyResult) {
            return (
                <div style={{ textAlign: "center", padding: "32px 20px" }}>
                    <Ico
                        size={"40"}
                        style={{ color: "#52c41a", marginBottom: 12 }}
                    >
                        check_circle
                    </Ico>
                    <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>
                        {labels.done}
                    </div>
                    <div style={{ fontSize: 14, color: "#666" }}>
                        <strong>{applyResult.applied}</strong> {labels.applied}
                        {applyResult.failed > 0 && (
                            <span style={{ color: "#ff4d4f", marginLeft: 8 }}>
                                <strong>{applyResult.failed}</strong> {labels.failedLabel}
                            </span>
                        )}
                    </div>
                    <div style={{ marginTop: 20, display: "flex", justifyContent: "center", gap: 8 }}>
                        <ButtonH sm ghost onClick={handleClose}>
                            {labels.close}
                        </ButtonH>
                        <ButtonH
                            sm
                            ghost
                            onClick={() => runScan(activeStep)}
                        >
                            {labels.runAgain}
                        </ButtonH>
                    </div>
                </div>
            );
        }

        return null;
    };

    // ── Main render ───────────────────────────────────
    return (
        <>
            <style>{spinKeyframes}</style>
            <div className="_f _fm" style={{ gap: 4, flexWrap: "wrap" }}>
                {/* Названия рядом с иконками, а не только в подсказке: четыре одинаковых серых
                    значка ничего не говорят о том, какая проверка запустится. */}
                {STEPS.map((step) => (
                    <ButtonH
                        key={step.key}
                        sm
                        ghost
                        onClick={() => runScan(step)}
                        disabled={loadingStep !== null}
                        style={{ padding: "2px 8px", minWidth: 0 }}
                    >
                        <Ico
                            size={"16"}
                            style={
                                loadingStep === step.key
                                    ? {
                                          animation: "tppSpin 1s linear infinite",
                                          display: "inline-block",
                                      }
                                    : undefined
                            }
                        >
                            {step.icon}
                        </Ico>
                        {fromEng ? step.labelEn : step.labelRu}
                    </ButtonH>
                ))}

                {/* Minimized indicator */}
                {minimized && proposals.length > 0 && (
                    <ButtonH
                        sm
                        onClick={() => {
                            setMinimized(false);
                            setShowModal(true);
                        }}
                        style={{
                            padding: "2px 10px",
                            minWidth: 0,
                            backgroundColor: "#e6f7ff",
                            borderColor: "#91d5ff",
                            color: "#096dd9",
                        }}
                    >
                        <Ico size={"14"}>open_in_full</Ico>
                        <span style={{ marginLeft: 4, fontSize: 12, fontWeight: 600 }}>
                            {proposals.length}
                        </span>
                    </ButtonH>
                )}
            </div>

            <Modal
                modalRender={(modal) => (
                    <div
                        style={{
                            transform: `translate(${offsetPos.dx}px, ${offsetPos.dy}px)`,
                        }}
                    >
                        {modal}
                    </div>
                )}
                title={
                    <div
                        style={{
                            cursor: "move",
                            userSelect: "none",
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                        }}
                        onMouseDown={onTitleMouseDown}
                    >
                        <span style={{ flex: 1 }}>
                            {labels.modalTitle}
                            {activeStep && (
                                <Tag color="blue" style={{ marginLeft: 8 }}>
                                    {fromEng ? activeStep.labelEn : activeStep.labelRu}
                                </Tag>
                            )}
                        </span>
                        <span
                            className="material-icons"
                            style={{
                                fontSize: 18,
                                cursor: "pointer",
                                color: "#999",
                                marginRight: 24,
                                padding: 4,
                            }}
                            onClick={(e) => {
                                e.stopPropagation();
                                setMinimized(true);
                                setShowModal(false);
                            }}
                            title={fromEng ? "Minimize" : "Свернуть"}
                        >
                            minimize
                        </span>
                    </div>
                }
                open={showModal && !minimized}
                onCancel={handleClose}
                width={900}
                footer={null}
            >
                {renderModalBody()}
            </Modal>
        </>
    );
};
